import { supabase } from "./supabase";
import {
  type Disk,
  type DiskAggregateResponse,
  type DiskAggregateRow,
  type DiskRow,
  diskFromRow,
  type SearchResponse,
  type SearchResultRow,
  type TreeNode,
} from "../types";

interface SearchParams {
  q: string;
  ext?: string[];
  diskId?: string;
  isDirectory?: boolean;
  minSize?: number;
  maxSize?: number;
  limit?: number;
  offset?: number;
}

interface DiskSearchParams {
  q: string;
  ext?: string[];
  isDirectory?: boolean;
  minSize?: number;
  maxSize?: number;
}

export const api = {
  async listDisks(): Promise<Disk[]> {
    const { data, error } = await supabase
      .from("disks")
      .select("*")
      .order("scanned_at", { ascending: false });
    if (error) throw error;
    return ((data as DiskRow[] | null) ?? []).map(diskFromRow);
  },

  async getDisk(id: string): Promise<Disk | null> {
    const { data, error } = await supabase.from("disks").select("*").eq("id", id).maybeSingle();
    if (error) throw error;
    return data ? diskFromRow(data as DiskRow) : null;
  },

  async createDisk(input: {
    label: string;
    volumeName?: string;
    volumeUuid?: string;
    totalSize?: number;
    notes?: string;
    scannedBy?: string;
  }): Promise<Disk> {
    const { data, error } = await supabase
      .from("disks")
      .insert({
        label: input.label,
        volume_name: input.volumeName ?? null,
        volume_uuid: input.volumeUuid ?? null,
        total_size: input.totalSize ?? null,
        notes: input.notes ?? null,
        scanned_by: input.scannedBy ?? null,
      })
      .select("*")
      .single();
    if (error) throw error;
    return diskFromRow(data as DiskRow);
  },

  async finalizeDisk(id: string, payload: { fileCount: number; usedSize: number }): Promise<void> {
    const { error } = await supabase
      .from("disks")
      .update({ file_count: payload.fileCount, used_size: payload.usedSize })
      .eq("id", id);
    if (error) throw error;
  },

  async deleteDisk(id: string): Promise<void> {
    const { error } = await supabase.from("disks").delete().eq("id", id);
    if (error) throw error;
  },

  async updateDisk(id: string, body: { label?: string; notes?: string | null }): Promise<Disk> {
    const update: Record<string, unknown> = {};
    if (body.label !== undefined) update.label = body.label;
    if (body.notes !== undefined) update.notes = body.notes;
    const { data, error } = await supabase
      .from("disks")
      .update(update)
      .eq("id", id)
      .select("*")
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error("Disque introuvable");
    return diskFromRow(data as DiskRow);
  },

  async findDiskByLabel(label: string): Promise<Disk | null> {
    const { data, error } = await supabase
      .from("disks")
      .select("*")
      .eq("label", label)
      .maybeSingle();
    if (error) throw error;
    return data ? diskFromRow(data as DiskRow) : null;
  },

  async diskTree(diskId: string, parentPath: string | null): Promise<TreeNode[]> {
    let query = supabase
      .from("files")
      .select("name, path, is_directory, size_bytes, modified_at_fs")
      .eq("disk_id", diskId)
      .order("is_directory", { ascending: false })
      .order("name", { ascending: true })
      .limit(2000);

    if (parentPath === null) {
      query = query.is("parent_path", null);
    } else {
      query = query.eq("parent_path", parentPath);
    }

    const { data, error } = await query;
    if (error) throw error;

    type Row = {
      name: string;
      path: string;
      is_directory: boolean;
      size_bytes: number | null;
      modified_at_fs: string | null;
    };

    return ((data as Row[] | null) ?? []).map((r) => ({
      name: r.name,
      path: r.path,
      isDirectory: r.is_directory,
      sizeBytes: r.size_bytes,
      modifiedAtFs: r.modified_at_fs,
    }));
  },

  async search(params: SearchParams): Promise<SearchResponse> {
    const t0 = performance.now();
    const term = params.q.normalize("NFC").trim();
    const ilikePattern = `%${term.replace(/[%_\\]/g, (c) => `\\${c}`)}%`;

    let query = supabase
      .from("files")
      .select(
        "id, disk_id, path, name, parent_path, is_directory, extension, size_bytes, modified_at_fs, disks!inner(label)",
        { count: "exact" }
      )
      .or(`name.ilike.${ilikePattern},path.ilike.${ilikePattern}`)
      .order("modified_at_fs", { ascending: false, nullsFirst: false })
      .range(params.offset ?? 0, (params.offset ?? 0) + (params.limit ?? 100) - 1);

    if (params.diskId) query = query.eq("disk_id", params.diskId);
    if (params.isDirectory !== undefined) query = query.eq("is_directory", params.isDirectory);
    if (params.ext && params.ext.length > 0) query = query.in("extension", params.ext);
    if (params.minSize !== undefined) query = query.gte("size_bytes", params.minSize);
    if (params.maxSize !== undefined) query = query.lte("size_bytes", params.maxSize);

    const { data, count, error } = await query;
    if (error) throw error;

    type RawRow = {
      id: number;
      disk_id: string;
      path: string;
      name: string;
      parent_path: string | null;
      is_directory: boolean;
      extension: string | null;
      size_bytes: number | null;
      modified_at_fs: string | null;
      disks: { label: string } | { label: string }[] | null;
    };

    const rows: SearchResultRow[] = ((data as RawRow[] | null) ?? []).map((r) => {
      const disk = Array.isArray(r.disks) ? r.disks[0] : r.disks;
      return {
        id: r.id,
        diskId: r.disk_id,
        diskLabel: disk?.label ?? "?",
        path: r.path,
        name: r.name,
        parentPath: r.parent_path,
        isDirectory: r.is_directory,
        extension: r.extension,
        sizeBytes: r.size_bytes,
        modifiedAtFs: r.modified_at_fs,
      };
    });

    return {
      total: count ?? rows.length,
      rows,
      tookMs: Math.round(performance.now() - t0),
    };
  },

  async searchByDisks(params: DiskSearchParams): Promise<DiskAggregateResponse> {
    const t0 = performance.now();
    const term = params.q.normalize("NFC").trim();

    const { data, error } = await supabase.rpc("search_disk_aggregates", {
      q: term,
      ext: params.ext && params.ext.length > 0 ? params.ext : null,
      is_dir: params.isDirectory ?? null,
      min_size: params.minSize ?? null,
      max_size: params.maxSize ?? null,
    });
    if (error) throw error;

    type RawRow = {
      disk_id: string;
      disk_label: string;
      disk_scanned_at: string | null;
      file_count: number | string;
      total_size: number | string | null;
      last_modified: string | null;
    };

    const rows: DiskAggregateRow[] = ((data as RawRow[] | null) ?? []).map((r) => ({
      diskId: r.disk_id,
      diskLabel: r.disk_label,
      diskScannedAt: r.disk_scanned_at,
      fileCount: Number(r.file_count),
      totalSize: Number(r.total_size ?? 0),
      lastModified: r.last_modified,
    }));

    return {
      rows,
      total: rows.length,
      tookMs: Math.round(performance.now() - t0),
    };
  },
};
